<template>
  <div>

    <v-toolbar elevation=0 class="users-toolbar mb-1 px-2" rounded>

      <CreateUserDialog v-if="$canControlUser()" @userCreated="openUserPermissionsDialog">
        <v-btn elevation='0' class="bg-primary" data-cy="userManagement-createUser-btn">
          <v-icon>mdi-account-plus-outline</v-icon>
          <span class="hidden-sm-and-down">
          {{$t('routes.users.createUser')}}
          </span>
        </v-btn>
      </CreateUserDialog>

      <div class="ml-1">
        <v-menu v-if="$canControlUser()"
                v-model="showingMenu"
                :persistent="false"
                close-on-content-click
                location="bottom"
                v-bind:disabled="selectedUsers.length === 0"
        >
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              v-bind:disabled="selectedUsers.length === 0"
              elevation=0
              class="bg-primary"
            >
              <v-icon>mdi-hexagon-multiple-outline</v-icon>

              <span class="hidden-sm-and-down">
              {{ $t('routes.users.bulkActions') }}
              </span>
            </v-btn>
          </template>

          <v-list data-cy="userManagement-options-list">
            <v-list-item v-on:click="showDeleteUsersDialog()" v-bind:disabled="selectedUsers.length === 0" data-cy="userManagement-options-deleteUsers">
              <template v-slot:prepend>
                <v-icon size="small">mdi-account-off-outline</v-icon>
              </template>

              <v-list-item-title>
                {{$t('routes.users.deleteUsers')}}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>

      <DeleteUsersDialog
        v-if="$canControlUser()"
        v-model="showingDeleteUsersDialog"
        :users="selectedUsers"
        @usersDeleted="afterUsersDeleted"
      />

      <v-text-field
        :label="$t('routes.users.nameOrEmail')"
        variant="solo-filled"
        flat
        hide-details
        append-inner-icon="mdi-magnify"
        class="ml-2"
        @update:model-value="filterBySearchQuery"
        v-model="searchQuery"
      />
    </v-toolbar>

    <v-row>
      <v-col>
        <UsersListView v-bind:userList="users" v-bind:fetchingUsers="fetchingUsers"></UsersListView>
      </v-col>
    </v-row>




    <UserPermissionsDialog
        ref="userPermissionsDialog"
        :getEditUser="createdUser"
        :accessPointId="accessPointId"
    ></UserPermissionsDialog>
  </div>
</template>

<script>
import { useTheme } from 'vuetify';
import { useAuthStore } from '@/stores/AuthStore.js'
import { useUserStore } from '@/stores/UserStore.js'
import { useNodeStore } from '@/stores/NodeStore.js';
import UsersListView from './components/UsersListView.vue'
import CreateUserDialog from './components/dialogs/CreateUserDialog.vue';
import DeleteUsersDialog from './components/dialogs/DeleteUsersDialog.vue';
import UserPermissionsDialog from './components/dialogs/UserPermissionsDialog.vue';

export default {
  name: "UsersIndex",
  components: {
    UsersListView,
    CreateUserDialog,
    DeleteUsersDialog,
    UserPermissionsDialog,
  },
  data: function() {
    const theme = useTheme();

    return {
      searchQuery: '',
      sortBy: '',
      sortDesc: false,
      showingMenu: false,
      showUserPermissionsDialog: false,
      createdUser: {
        abilities: [],
      },
      accessPointId: null,
      users: [],
      fetchingUsers: true,

      theme: theme.global.name.value,

      showingDeleteUsersDialog: false,
    };
  },
  created: function() {
    if (!this.$canListUser()) {
      this.$router.push('/console/index');
    }
  },
  watch: {
    originUsers: function(newValue) {
      this.users = newValue;

      if (this.searchQuery !== '') {
        this.filterBySearchQuery(this.searchQuery);
      }
    },
  },
  mounted: async function() {
    if (!this.user) {
      const authStore = useAuthStore();
      await authStore.refreshDBUser();
    }

    this.fetchData();
  },
  methods: {
    // goBack: function() {
    //   this.$router.go(-1);
    // },
    filterBySearchQuery: function(searchInput = null) {
      if (searchInput === '') {
        this.users = this.originUsers;
      }

      const cleanQuery = searchInput.toLowerCase();

      const filteredUsers = this.originUsers.filter((user) => {
        return `${user.firstname} ${user.lastname}`.toLowerCase().includes(cleanQuery) || user.email.toLowerCase().includes(cleanQuery);
      });

      this.users = filteredUsers;
    },
    showDeleteUsersDialog() {
      this.showingDeleteUsersDialog = true;
    },
    openUserPermissionsDialog: function(payload) {
      this.createdUser = payload.user;
      this.accessPointId = payload.accessPointId;

      this.$refs.userPermissionsDialog.openDialog(true);

      const userStore = useUserStore();
      userStore.refreshUsers({ accessPointId: this.activeAccessPointId });
    },
    fetchData: async function() {
      this.fetchingUsers = true;


      try {
        const nodeStore = useNodeStore();
        // await nodeStore.refreshAccessPoints();

        const userStore = useUserStore();
        // await userStore.refreshUsertypes();

        const authStore = useAuthStore();
        const accessPointId = authStore.rootNodeId;

        // Refresh users for this access point
        // await userStore.refreshUsers({ accessPointId });

        await Promise.all([
          userStore.refreshUsertypes(),
          userStore.refreshUsers({ accessPointId }),
          nodeStore.refreshAccessPoints(),
        ]);
      } catch (error) {
        console.error('Error fetching data', error);
      } finally {
        this.fetchingUsers = false;
      }
    },
    afterUsersDeleted: function() {
      const successMessage = this.$t('routes.users.usersDeleted', this.users.length);
      this.$showMessage(successMessage);

      const userStore = useUserStore();
      userStore.resetSelectedUsers();
      userStore.refreshUsers({ accessPointId: this.activeAccessPointId });
    },
    findAccessPoint: function(payload) {
      // Check if the necessary nested properties exist
      if (
          !payload ||
          !payload.user ||
          !payload.user.tenants ||
          !Array.isArray(payload.user.accessPoints)
      ) {
        console.error('Invalid payload structure');
        return null;
      }

      return payload.user.accessPoints.find((ap) => ap.node.id === payload.accessPointId);
    }
  },
  computed: {
    selectedUsers: function() {
      const userStore = useUserStore();
      return userStore.getSelectedUsers;
    },
    user: function() {
      const authStore = useAuthStore();
      return authStore.dbUser;
    },
    originUsers: function() {
      const userStore = useUserStore();
      return userStore.getUsers;
    },
    activeAccessPointId: function() {
      const authStore = useAuthStore();
      return authStore.rootNodeId;
    },
  },
}
</script>

<style scoped>
:deep(.v-theme--dark).users-toolbar {
  background-color: #272727 !important;
}

:deep(.v-theme--light).users-toolbar {
  background-color: white !important;
}
</style>
